import { planName } from '@/shared/config/plans';
import { daysUntil, formatMonthDay } from '@/shared/lib/format';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import type { UseCheckoutFlowResult } from '../hooks/use-checkout-flow';
import { paymentMethodLabel } from '../model/billing-status';
import { changeRows } from '../model/checkout-summary';
import type { PlanChangePreviewData, WorkspaceData } from '../types';
import { SummaryList } from './summary-list';

type ChangePlanSummaryProps = {
  workspace: WorkspaceData;
  flow: UseCheckoutFlowResult;
  preview: PlanChangePreviewData | null;
  isLoading: boolean;
};

/** Moving between paid plans: Paddle's prorated preview, then one button (design: Checkout, change). */
export function ChangePlanSummary({ workspace, flow, preview, isLoading }: ChangePlanSummaryProps) {
  const { billing } = workspace;
  const periodEnd = billing.subscription?.currentPeriodEndsAt ?? null;
  const name = planName(flow.plan);
  const isUpgrade = preview?.isUpgrade ?? true;
  const label = isUpgrade
    ? `Upgrade to ${name}`
    : `Move to ${name} on ${periodEnd ? formatMonthDay(periodEnd) : 'the next period'}`;

  return (
    <>
      {preview ? (
        <SummaryList rows={changeRows(flow.plan, preview, periodEnd ? daysUntil(periodEnd) : 0)} />
      ) : null}
      {!preview && isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton isPulsing />
          <Skeleton isPulsing />
        </div>
      ) : null}
      {flow.isPaymentsOff ? null : (
        <>
          <Button
            variant="primary"
            size="lg"
            isBlock
            disabled={!preview || flow.plan === flow.currentPlan}
            isLoading={flow.isChanging}
            onClick={() => void flow.confirmChange()}
          >
            {label}
          </Button>
          <p className="text-meta text-muted">
            Charged to {paymentMethodLabel(billing.paymentMethod)} through Paddle. Moving down to a
            smaller plan takes effect at the end of the period.
          </p>
        </>
      )}
    </>
  );
}
